import apiClient from './apiClient';
import { ApiResponse, PaginatedResponse } from '../../types/common';
import {
  MalwareBlacklist,
  BlacklistFormData,
  AppWhitelist,
  WhitelistFormData,
  ApkAnalysis,
  AnalysisFilters,
  DeepfakeAnalysis,
  DeepfakeFilters,
  VirusScanResult,
  VirusScanFilters,
} from '../../types/analysis';

// Blacklist APIs
export const getBlacklist = async (
  filters: Partial<AnalysisFilters>
): Promise<PaginatedResponse<MalwareBlacklist>> => {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<MalwareBlacklist>>>(
    '/admin/security/blacklist',
    { params: filters }
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || '블랙리스??조회???�패?�습?�다.');
  }

  return response.data.data;
};

export const createBlacklist = async (data: BlacklistFormData): Promise<MalwareBlacklist> => {
  const response = await apiClient.post<ApiResponse<MalwareBlacklist>>(
    '/admin/security/blacklist',
    data
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || '블랙리스??추�????�패?�습?�다.');
  }

  return response.data.data;
};

export const updateBlacklist = async (
  id: number,
  data: Partial<BlacklistFormData>
): Promise<MalwareBlacklist> => {
  const response = await apiClient.put<ApiResponse<MalwareBlacklist>>(
    `/admin/security/blacklist/${id}`,
    data
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || '블랙리스트 수정에 실패했습니다.');
  }

  return response.data.data;
};

export const deleteBlacklist = async (id: number): Promise<void> => {
  const response = await apiClient.delete<ApiResponse<void>>(`/admin/security/blacklist/${id}`);

  if (response.data.status !== 200) {
    throw new Error(response.data.message || '블랙리스트 삭제에 실패했습니다.');
  }
};

// APK Analysis APIs
export const getApkAnalyses = async (
  filters: AnalysisFilters
): Promise<PaginatedResponse<ApkAnalysis>> => {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<ApkAnalysis>>>(
    '/admin/security/analysis/apk',
    { params: filters }
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || 'APK 분석 목록 조회???�패?�습?�다.');
  }

  return response.data.data;
};

export const getApkAnalysisById = async (sha256: string): Promise<ApkAnalysis> => {
  const response = await apiClient.get<ApiResponse<ApkAnalysis>>(
    `/admin/security/analysis/apk/${sha256}`
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || 'APK 분석 상세 조회에 실패했습니다.');
  }

  return response.data.data;
};

// Deepfake Analysis APIs
export const getDeepfakeAnalyses = async (
  filters: DeepfakeFilters
): Promise<PaginatedResponse<DeepfakeAnalysis>> => {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<DeepfakeAnalysis>>>(
    '/admin/security/analysis/deepfake',
    { params: filters }
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || '?�페?�크 분석 목록 조회???�패?�습?�다.');
  }

  return response.data.data;
};

// Whitelist APIs
export const getWhitelist = async (
  filters: Partial<AnalysisFilters>
): Promise<PaginatedResponse<AppWhitelist>> => {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<AppWhitelist>>>(
    '/admin/security/whitelist',
    { params: filters }
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || '?�이?�리?�트 조회???�패?�습?�다.');
  }

  return response.data.data;
};

export const createWhitelist = async (data: WhitelistFormData): Promise<AppWhitelist> => {
  const response = await apiClient.post<ApiResponse<AppWhitelist>>(
    '/admin/security/whitelist',
    data
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || '?�이?�리?�트 추�????�패?�습?�다.');
  }

  return response.data.data;
};

export const updateWhitelist = async (
  id: number,
  data: Partial<WhitelistFormData>
): Promise<AppWhitelist> => {
  const response = await apiClient.put<ApiResponse<AppWhitelist>>(
    `/admin/security/whitelist/${id}`,
    data
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || '화이트리스트 수정에 실패했습니다.');
  }

  return response.data.data;
};

export const deleteWhitelist = async (id: number): Promise<void> => {
  const response = await apiClient.delete<ApiResponse<void>>(`/admin/security/whitelist/${id}`);

  if (response.data.status !== 200) {
    throw new Error(response.data.message || '화이트리스트 삭제에 실패했습니다.');
  }
};

// CSV 업로드 결과 타입
export interface CsvUploadResult {
  total: number;
  success: number;
  csvDuplicate: number;
  dbDuplicate: number;
  skipped: number;
  failed: number;
  errors: Array<{
    row: number;
    message: string;
    data: any;
  }>;
}

export const uploadWhitelistCsv = async (file: File): Promise<CsvUploadResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<ApiResponse<CsvUploadResult>>(
    '/admin/security/whitelist/csv',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || 'CSV 업로드에 실패했습니다.');
  }

  return response.data.data;
};

// Virus Scan APIs
export const getVirusScans = async (
  filters: Partial<VirusScanFilters>
): Promise<PaginatedResponse<VirusScanResult>> => {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<VirusScanResult>>>(
    '/admin/security/analysis/virus-scan',
    { params: filters }
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || '바이러스 스캔 목록 조회에 실패했습니다.');
  }

  return response.data.data;
};

export const getVirusScanById = async (sha256: string): Promise<VirusScanResult> => {
  const response = await apiClient.get<ApiResponse<VirusScanResult>>(
    `/admin/security/analysis/virus-scan/${sha256}`
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || '바이러스 스캔 상세 조회에 실패했습니다.');
  }

  return response.data.data;
};

// User Scan History APIs
export interface UserScanHistory {
  id: string;
  userId: string;
  scanType: string;
  totalApps: number;
  safeApps: number;
  lowRiskApps: number;
  mediumRiskApps: number;
  highRiskApps: number;
  threatsFound: number;
  scanDuration: number;
  deviceInfo: {
    model?: string;
    brand?: string;
    systemVersion?: string;
    appVersion?: string;
  };
  threatDetails: Array<{
    packageName: string;
    appName: string;
    riskLevel: string;
    threats: string[];
    score: number;
  }>;
  createdAt: string;
  user?: {
    id: string;
    email: string;
    username: string;
  };
}

export interface UserScanHistoryFilters {
  page?: number;
  limit?: number;
  userId?: string;
  minThreats?: number;
}

export const getUserScanHistory = async (
  filters: UserScanHistoryFilters
): Promise<PaginatedResponse<UserScanHistory>> => {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<UserScanHistory>>>(
    '/admin/security/user-scan-history',
    { params: filters }
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || '유저 스캔 내역 조회에 실패했습니다.');
  }

  return response.data.data;
};

export const getUserScanHistoryById = async (id: string): Promise<UserScanHistory> => {
  const response = await apiClient.get<ApiResponse<UserScanHistory>>(
    `/admin/security/user-scan-history/${id}`
  );

  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || '유저 스캔 내역 상세 조회에 실패했습니다.');
  }

  return response.data.data;
};

// Spam Phone Number APIs
export interface SpamPhoneNumber {
  id?: string;
  phoneNumber: string;
  reportCount: number;
  approvedCount: number;
  pendingCount: number;
  lastReportedAt: string;
  displayLabel?: string | null;
}

export interface SpamPhoneReport {
  id: string;
  phoneNumber: string;
  reportType: string;
  description: string;
  status: string;
  displayLabel: string | null;
  createdAt: string;
  reporter?: { id: string; email: string; username: string };
}

export interface SpamPhoneFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export interface SpamPhoneFormData {
  phoneNumber: string;
  reportType: string;
  description?: string;
  displayLabel?: string;
}

export const getSpamPhoneNumbers = async (
  filters: SpamPhoneFilters
): Promise<PaginatedResponse<SpamPhoneNumber>> => {
  const response = await apiClient.get<ApiResponse<PaginatedResponse<SpamPhoneNumber>>>(
    '/admin/reports/spam-numbers',
    { params: filters }
  );
  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || '스팸 번호 목록 조회에 실패했습니다.');
  }
  return response.data.data;
};

export const getSpamPhoneReports = async (phoneNumber: string): Promise<SpamPhoneReport[]> => {
  const response = await apiClient.get<ApiResponse<SpamPhoneReport[]>>(
    `/admin/reports/spam-numbers/${phoneNumber}`
  );
  if (response.data.status !== 200 || !response.data.data) {
    throw new Error(response.data.message || '신고 내역 조회에 실패했습니다.');
  }
  return response.data.data;
};

export const createSpamPhoneNumber = async (data: SpamPhoneFormData): Promise<void> => {
  const response = await apiClient.post<ApiResponse<void>>('/admin/reports/spam-numbers', data);
  if (response.data.status !== 200) {
    throw new Error(response.data.message || '스팸 번호 등록에 실패했습니다.');
  }
};

export const approveAllSpamReports = async (
  phoneNumber: string,
  displayLabel?: string
): Promise<void> => {
  const response = await apiClient.put<ApiResponse<void>>(
    `/admin/reports/spam-numbers/${phoneNumber}/approve-all`,
    { displayLabel }
  );
  if (response.data.status !== 200) {
    throw new Error(response.data.message || '일괄 승인에 실패했습니다.');
  }
};

export const deleteSpamPhoneNumber = async (phoneNumber: string): Promise<void> => {
  const response = await apiClient.delete<ApiResponse<void>>(
    `/admin/reports/spam-numbers/${phoneNumber}`
  );
  if (response.data.status !== 200) {
    throw new Error(response.data.message || '스팸 번호 삭제에 실패했습니다.');
  }
};

export const updateSpamPhoneLabel = async (
  phoneNumber: string,
  displayLabel: string
): Promise<void> => {
  const response = await apiClient.put<ApiResponse<void>>(
    `/admin/reports/spam-numbers/${phoneNumber}/label`,
    { displayLabel }
  );
  if (response.data.status !== 200) {
    throw new Error(response.data.message || '표시 텍스트 수정에 실패했습니다.');
  }
};
